import {memo, MouseEvent, useCallback, useMemo} from 'react';
import {SchoolData as SchoolDataBase} from '@/map/@types';
import {AROUND_DATA_OPTIONS, MAX_VISIBLE_ZOOM_METER} from '@/map/constants';
import {useVisibleZoomLevel} from '@/map/hooks';
import {convertToCoordFromPointCoords, getSchoolFilterNameByKoreanType} from '@/map/utils';
import {SpotMarker} from '../../Marker';

type SchoolData = Omit<SchoolDataBase, 'gradeStudentCount'>;

interface IProps {
  map?: naver.maps.Map;
  items?: SchoolData[];
  selectedItem?: SchoolData;
  onClick?(selected: {index: number}): void;
}

interface MarkerProps {
  map: naver.maps.Map;
  id: string;
  item: SchoolData;
  active?: boolean;
  onClick?(e: MouseEvent<HTMLDivElement>): void;
}

export const SchoolMarkers = ({map, items, selectedItem, onClick}: IProps) => {
  const isVisibleZoom = useVisibleZoomLevel(
    MAX_VISIBLE_ZOOM_METER.mobile_markers
  );

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const id = e.currentTarget.dataset.id;

      if (id) {
        onClick?.({index: Number(id)});
      }
    },
    [onClick]
  );

  if (!map || !items || !isVisibleZoom) {
    return null;
  }

  return (
    <>
      {items.map((i, idx) => (
        <Marker
          key={idx}
          map={map}
          id={String(idx)}
          item={i}
          active={selectedItem?.name === i.name}
          onClick={handleClick}
        />
      ))}
    </>
  );
};

const Marker = memo(({item, ...props}: MarkerProps) => {
  const position = useMemo(
    () => convertToCoordFromPointCoords(item?.location?.coordinates),
    [item?.location?.coordinates]
  );
  const typeInfo = useMemo(() => {
    const type = getSchoolFilterNameByKoreanType(item.type);

    return AROUND_DATA_OPTIONS[type];
  }, [item.type]);

  if (!item || !position || !typeInfo) {
    return null;
  }

  return (
    <SpotMarker
      {...props}
      position={position}
      name={item.name}
      color={typeInfo.mobile?.color}
      typeLabel={typeInfo.label}
    />
  );
});
